import { useRef, useState, type ChangeEvent } from "react";
import { motion } from "framer-motion";
import { Camera, LogOut, Moon, Save, Sun, SunMoon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/atoms/Avatar";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/atoms/Card";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import { DeleteAccountDialog } from "@/modules/identity/components/DeleteAccountDialog";
import { PhotoCropDialog } from "@/modules/identity/components/PhotoCropDialog";
import { StorageService } from "@/services/StorageService";
import { leagueRepository } from "@/config/dataSource";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/utils/cn";
import type { Theme } from "@/types/user";

const THEME_OPTIONS: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
  { value: "light", label: "Claro", icon: Sun },
  { value: "dark", label: "Escuro", icon: Moon },
  { value: "system", label: "Automático", icon: SunMoon },
];

export function ProfilePage() {
  const { profile, refreshProfile, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [nome, setNome] = useState(profile?.nome ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoToCrop, setPhotoToCrop] = useState<File | null>(null);

  const dirty = nome.trim() !== "" && nome !== profile?.nome;
  const firstName = profile?.nome?.split(" ")[0] ?? "";

  async function handleSaveName() {
    if (!profile) return;
    setIsSaving(true);
    try {
      const nomeTrimmed = nome.trim();
      await AuthenticationService.updateUserProfile(profile.uid, { nome: nomeTrimmed });
      // Mesma sincronização já usada pra foto (ver comentário mais abaixo)
      // — sem isto, o nome ficava preso no valor de quando entrou em cada
      // liga, aparecendo desatualizado no Ranking, Pódio e X1.
      void leagueRepository.syncProfileAcrossLeagues({ nome: nomeTrimmed });
      await refreshProfile();
      toast({ variant: "success", title: "Perfil atualizado" });
    } catch {
      toast({ variant: "destructive", title: "Não foi possível salvar", description: "Tente novamente em instantes." });
    } finally {
      setIsSaving(false);
    }
  }

  function handlePhotoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // permite escolher o mesmo arquivo de novo depois
    if (!file) return;
    setPhotoToCrop(file); // abre o diálogo de recorte — upload só acontece depois de "Cortar e salvar"
  }

  async function handleCropped(blob: Blob) {
    if (!profile) return;
    setPhotoToCrop(null);
    setIsUploadingPhoto(true);
    try {
      const url = await StorageService.uploadUserPhoto(profile.uid, blob);
      await AuthenticationService.updateUserProfile(profile.uid, { foto: url });
      await refreshProfile();
      // Sincroniza a cópia da foto em toda liga que o usuário participa —
      // sem isto, o Ranking/Pódio continuariam mostrando a foto antiga
      // (ex: a do Google usada no login) pra sempre, mesmo depois de
      // trocar aqui. Melhor esforço: nunca lança, nunca atrapalha o toast
      // de sucesso acima mesmo que essa parte falhe.
      void leagueRepository.syncProfileAcrossLeagues({ avatarUrl: url });
      toast({ variant: "success", title: "Foto atualizada" });
    } catch (err) {
      // Log real do erro — antes disso, qualquer falha (regra do Storage,
      // regra do Firestore, CORS do bucket, rede) virava a MESMA mensagem
      // genérica, sem nenhum jeito de saber a causa real. Agora aparece
      // no console do navegador (F12 → Console) com detalhe suficiente
      // pra diagnosticar sem chute.
      console.error("[ProfilePage] falha ao trocar foto de perfil:", err);
      const message = err instanceof Error ? err.message : "Erro desconhecido.";
      toast({ variant: "destructive", title: "Não foi possível enviar a foto", description: message });
    } finally {
      setIsUploadingPhoto(false);
    }
  }

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-3 pt-2">
        <div className="relative">
          <Avatar size="xl">
            <AvatarImage src={profile?.foto ?? undefined} alt={profile?.nome ?? "Usuário"} />
            <AvatarFallback>{firstName.charAt(0).toLocaleUpperCase() || "P"}</AvatarFallback>
          </Avatar>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingPhoto}
            className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-soft transition-transform hover:scale-105 disabled:opacity-60"
            aria-label="Alterar foto de perfil"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
        </div>
        <div className="text-center">
          <p className="font-display text-lg font-bold">{profile?.nome}</p>
          <p className="text-sm text-muted-foreground">{profile?.email}</p>
        </div>
      </motion.div>

      <Card>
        <CardHeader>
          <CardTitle>Dados pessoais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="nome">Nome</Label>
            <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <Button size="sm" onClick={handleSaveName} disabled={!dirty} isLoading={isSaving}>
            <Save className="h-4 w-4" /> Salvar alterações
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Aparência</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-xl border border-border p-3 text-xs font-medium transition-colors",
                  theme === value ? "border-primary bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200" : "hover:bg-muted"
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button variant="outline" className="w-full text-destructive hover:bg-destructive/10" onClick={() => signOut()}>
        <LogOut className="h-4 w-4" /> Sair da conta
      </Button>

      <DeleteAccountDialog />
      <PhotoCropDialog file={photoToCrop} onCancel={() => setPhotoToCrop(null)} onCropped={handleCropped} />
    </div>
  );
}
